import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import './index.css'
import { AuthProvider } from './contexts/AuthContext'
import { exposeDebugInfo, logEnvironmentInfo } from './utils/debugHelper'
import { getCurrentConfig } from './utils/apiConfig'
import { testSupabaseConnection } from './lib/test-connection'

// Run the connection test on startup in an async IIFE
;(async () => {
  await testSupabaseConnection();
})();

// EXTREMELY IMPORTANT ENVIRONMENT DETECTION
// This will always run in any environment, even in production builds
function performEnvironmentChecks() {
  try {
    const hostname = window.location.hostname;
    const isStaging = hostname.includes('staging');
    const isProd = hostname.includes('app.sequenceplanner.com');
    const isDev = hostname === 'localhost' || hostname.includes('127.0.0.1');
    
    // Force log to always show in console, regardless of build environment
    const forcePrint = (msg: string) => {
      // Only log in development or staging
      if (isDev || isStaging) {
        const style = 'background:#ff5722; color:white; padding:2px 6px; border-radius:3px; font-weight:bold;';
        console.log(`%c${msg}`, style);
      }
      
      // Only show visual debug banner in development mode or staging
      if (isDev || isStaging) {
        setTimeout(() => {
          try {
            const existingDebugDiv = document.getElementById('env-debug-banner');
            if (existingDebugDiv) return; // Don't add multiple

            const debugDiv = document.createElement('div');
            debugDiv.id = 'env-debug-banner'; // Add ID for checking
            debugDiv.style.position = 'fixed';
            debugDiv.style.top = '0';
            debugDiv.style.left = '0';
            debugDiv.style.width = '100%';
            debugDiv.style.padding = '10px';
            debugDiv.style.background = 'rgba(255,87,34,0.9)';
            debugDiv.style.color = 'white';
            debugDiv.style.zIndex = '9999';
            debugDiv.style.fontFamily = 'monospace';
            debugDiv.style.fontSize = '12px';
            debugDiv.style.maxHeight = '200px';
            debugDiv.style.overflow = 'auto';
            debugDiv.innerHTML = `<strong>ENV DEBUG [${new Date().toISOString()}]:</strong><br/>
              Hostname: ${hostname}<br/>
              Detected: ${isDev ? 'Development' : isStaging ? 'Staging' : isProd ? 'Production' : 'Unknown'}<br/>
              API URL: ${getCurrentConfig().apiUrl}`;
            
            // Add close button
            const closeBtn = document.createElement('button');
            closeBtn.innerText = 'Close';
            closeBtn.style.position = 'absolute';
            closeBtn.style.top = '5px';
            closeBtn.style.right = '5px';
            closeBtn.style.padding = '3px 8px';
            closeBtn.style.background = 'white';
            closeBtn.style.color = 'black';
            closeBtn.style.border = 'none';
            closeBtn.style.borderRadius = '3px';
            closeBtn.onclick = () => document.body.removeChild(debugDiv);
            debugDiv.appendChild(closeBtn);
            
            document.body.appendChild(debugDiv);
            
            // Auto-remove after 30 seconds
            setTimeout(() => {
              if (document.body.contains(debugDiv)) {
                document.body.removeChild(debugDiv);
              }
            }, 30000);
          } catch (e) {
            console.error('Failed to add debug div', e);
          }
        }, 1000);
      }
    };
    
    // Extremely visible logging that can't be missed
    forcePrint(`ENVIRONMENT CHECK: Running on ${hostname}`);
    forcePrint(`ENVIRONMENT DETECTED AS: ${isDev ? 'DEVELOPMENT' : isStaging ? 'STAGING' : isProd ? 'PRODUCTION' : 'UNKNOWN'}`);
    
    // Log API URLs
    const config = getCurrentConfig();
    forcePrint(`API URL: ${config.apiUrl}`);
  } catch (e) {
    console.error('Error in environment detection', e);
  }
}

// Call detection early
performEnvironmentChecks();

// Run connection test early
testSupabaseConnection().catch(err => console.error('Supabase', 'Initial connection test failed:', err));

// Initialize debug helpers in all environments
exposeDebugInfo();

// Log environment info at startup
console.log('App', '🔧 Starting application with configuration:');
const config = getCurrentConfig();
console.log('App', 'Environment:', config.environment);
console.log('App', 'API URL:', config.apiUrl);

// --- Refactored Functional Error Boundary --- 
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const FunctionalErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      // Always log errors even in production
      console.error('App Error (event listener):', event.error);
      setError(event.error);
      setHasError(true);
      // Prevent default browser error handling
      event.preventDefault(); 
    };

    const rejectionHandler = (event: PromiseRejectionEvent) => {
      // Always log errors even in production
      console.error('Unhandled Rejection:', event.reason);
      setError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
      setHasError(true);
      event.preventDefault();
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);

    // Cleanup
    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
    };
  }, []);

  if (hasError) {
    // Fallback UI
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-800 text-white p-4">
        <div className="max-w-md w-full bg-slate-700 p-6 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold mb-4 text-red-400">Something went wrong</h1>
          <p className="mb-4">The application encountered an error. Please try refreshing the page.</p>
          <div className="bg-slate-800 p-3 rounded text-slate-300 text-sm overflow-auto max-h-40 mb-4">
            <p>{error?.toString()}</p>
          </div>
          <button 
            onClick={() => window.location.href = '/'} 
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>; // Render children normally
};
// --- End Refactored Error Boundary ---

// Remove old global error listener
// window.addEventListener('error', (event) => { ... });

// Remove temporary loading div logic as it's less useful now

// Render the app using the new functional Error Boundary
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Failed to find the root element");
}

const root = ReactDOM.createRoot(rootElement);
try {
  console.log('App', 'Starting to render application...');
  root.render(
    <React.StrictMode>
      <FunctionalErrorBoundary>
        <AuthProvider>
          <App />
        </AuthProvider>
      </FunctionalErrorBoundary>
    </React.StrictMode>,
  );
  console.log('App', 'Application rendered successfully');
} catch (err) {
  // Always log errors even in production
  console.error('Failed to render application:', err);
}
